import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSupabaseClient } from '../providers/supabase';
import { useHtClient } from '../providers/ht';
import { saveQuizToken } from './quizTokens';

export type CategoryType = 'Dream Chaser' | 'Wealth Architect' | 'Safety Seeker' | 'Generous Altruist' | 'Freedom Finder';

export interface QuizAnswer {
  question_id: number;
  question: string;
  answer: string;
  category: CategoryType;
}
export interface Quiz {
  id: number;
  created_at: string;
  sf_account: string | null;
  answers: QuizAnswer[];
  results: {
    [K in CategoryType]: number;
  };
}

// Create a new quiz
export const useCreateQuiz = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sf_account?: string) => {
      if (!client) throw new Error("API client not initialized");
      
      console.log('Creating quiz with sf_account:', sf_account);
      const payload = { sf_account };
      console.log('Request payload:', payload);
      
      try {
        const response = await client.post("/quizzes", payload);
        const { write_token, ...quiz } = response.data || {};
        saveQuizToken(quiz.id, write_token);
        console.log('Quiz creation response:', quiz);
        return quiz;
      } catch (error: any) {
        console.error('Quiz creation error:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          detail: error.response?.data?.detail
        });
        throw error;
      }
    },
  });
};

// Submit quiz answers
export const useSubmitQuizAnswers = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      quizId, 
      answers,
      results
    }: { 
      quizId: number; 
      answers: QuizAnswer[];
      results: any;
    }) => {
      if (!client) throw new Error("API client not initialized");
      console.log("Submitting POST to client/supabase with answers and results:", answers, results);
      
      const response = await client.post(`/quizzes/${quizId}/answers`, { answers, results });
      return response.data;
    },
  });
};

// Get quiz results
export const useQuiz = (quizId: number) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['quiz', quizId],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.get(`/quizzes/${quizId}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get quizzes for a specific Salesforce account
export const useAccountQuizzes = (sfAccount: string) => {
  console.log("[useAccountQuizzes] HOOK CALLED with sfAccount:", sfAccount);
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['accountQuizzes', sfAccount],
    queryFn: async () => {
      try {
        console.log("[useAccountQuizzes] client:", client);
        console.log("[useAccountQuizzes] sfAccount:", sfAccount);
        if (!client) throw new Error("API client not initialized");
        const response = await client.get(`/quizzes?sf_account=${sfAccount}`);
        console.log("[useAccountQuizzes] API response:", response);
        return response.data;
      } catch (error) {
        console.error("[useAccountQuizzes] Query error:", error);
        throw error;
      }
    },
    enabled: !!sfAccount,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get quizzes for a specific user account
export const useProfileQuizzes = (profile: string | undefined) => {
  const { data: client } = useHtClient();

  return useQuery({
    queryKey: ['profileQuizzes', profile],
    queryFn: async () => {
      try {
        console.log("useProfileQuizzes with profile", profile);
        if (!client) throw new Error("API client not initialized");
        const response = await client.get(`/quizzes?profile=${profile}`);
        console.log("[useProfileQuizzes] API response:", response);
        return response.data;
        } catch (error) {
          console.error("[useAccountQuizzes] Query error:", error);
          throw error;
      }
    },
    enabled: !!profile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Create a lead
export const useCreateLead = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (leadData: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      sf_account?: string | null;
      quiz?: number | null;
      advisor_public_id?: string; // from an advisor's link/QR; backend resolves it
    }) => {
      if (!client) throw new Error("API client not initialized");
      
      // Retry logic for transient failures
      const maxRetries = 3;
      let lastError;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`👤 Creating lead (attempt ${attempt}/${maxRetries})`);
          const response = await client.post('/leads', leadData);
          console.log(`✅ Lead created successfully on attempt ${attempt}`);
          return response.data;
        } catch (error: any) {
          lastError = error;
          console.log(`❌ Lead creation failed on attempt ${attempt}:`, error.response?.status, error.message);
          
          // Don't retry on certain errors
          if (error.response?.status === 400 || error.response?.status === 422) {
            console.log('🚫 Not retrying - client error (400/422)');
            throw error;
          }
          
          if (attempt === maxRetries) {
            console.log('🚫 Max retries reached, giving up');
            break;
          }
          
          // Exponential backoff: 500ms, 1s, 2s
          const delay = Math.min(500 * Math.pow(2, attempt - 1), 2000);
          console.log(`⏳ Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      throw lastError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

// Update quiz with specific results
export const useUpdateQuizWithSpecificResults = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ quizId, answers }: { quizId: number; answers: QuizAnswer[] }) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.put(`/quizzes/${quizId}/specific-results`, { answers });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quiz', variables.quizId] });
      queryClient.invalidateQueries({ queryKey: ['accountQuizzes'] });
    }
  });
}; 

interface LeadPriorities {
  selected: string[];
  other: string;
}

// Update leads with user's priorities
export const useUpdateLeadWithPriorities = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quizId, priorities } : { quizId: number, priorities: LeadPriorities }) => {
      if (!client) throw new Error("API client not initialized");

      const response = await client.put(`/leads/quiz/${quizId}/priorities`, { priorities });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

export const useUpdateQuizWithAccount = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quizId, sfAccount }: { quizId: number; sfAccount: string }) => {
      if (!client) throw new Error("API client not initialized");
      const response = await client.put(`/quizzes/${quizId}/update/sfaccount`, { sfAccount });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quiz', variables.quizId] });
      queryClient.invalidateQueries({ queryKey: ['accountQuizzes'] });
    }
  });
};

export const useUpdateQuizWithSocials = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quizId, socials }: { quizId: number, socials: string[] }) => {
      if (!client) throw new Error("API client not initialized");
      const response = await client.put(`/quizzes/${quizId}/socials`, { socials });
      return response.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quiz', variables.quizId] });
      queryClient.invalidateQueries({ queryKey: ['accountQuizzes'] });
    }
  })
}

export const useUpdateQuizWithProfile = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quizId, profile }: { quizId: number; profile: string }) => {
      if (!client) throw new Error("API client not initialized");
      const response = await client.put(`/quizzes/${quizId}/update/profile`, { profile });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quiz', variables.quizId] });
      queryClient.invalidateQueries({ queryKey: ['accountQuizzes'] });
    }
  });
};

// Update quiz with info (personal info collected pre-signup)
export const useUpdateQuizWithInfo = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quizId, info }: { quizId: number; info: Record<string, string> }) => {
      if (!client) throw new Error("API client not initialized");
      const response = await client.put(`/quizzes/${quizId}/info`, { info });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quiz', variables.quizId] });
      queryClient.invalidateQueries({ queryKey: ['accountQuizzes'] });
    }
  });
};
